"use client"

import * as React from "react"
import { createPortal } from "react-dom"
import { Calendar as CalendarIcon } from "lucide-react"
import { Button } from "./button"
import { Calendar } from "./calendar"
import { Input } from "./input"
import { cn, DATE_INPUT_CLASSNAME } from "@/lib/utils"

interface DatePickerProps {
  value?: string
  onChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
  className?: string
}

export function DatePicker({
  value,
  onChange,
  disabled = false,
  placeholder = "Select date",
  className
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(
    value ? new Date(value) : undefined
  )
  const [inputValue, setInputValue] = React.useState<string>(value || "")
  const inputRef = React.useRef<HTMLDivElement>(null)
  const [calendarPosition, setCalendarPosition] = React.useState<{ top?: number; bottom?: number; left: number; width: number } | null>(null)
  const calendarRef = React.useRef<HTMLDivElement>(null)

  // Update selectedDate when value prop changes
  React.useEffect(() => {
    if (value) {
      const date = new Date(value)
      if (!isNaN(date.getTime())) {
        setSelectedDate(date)
        setInputValue(value)
      }
    } else {
      setSelectedDate(undefined)
      setInputValue("")
    }
  }, [value])

  // Calculate calendar position when opening
  React.useEffect(() => {
    if (open && inputRef.current) {
      const updatePosition = () => {
        if (inputRef.current) {
          const rect = inputRef.current.getBoundingClientRect()
          const spaceBelow = window.innerHeight - rect.bottom
          const spaceAbove = rect.top

          // Decide whether to open up or down
          // If less than 350px below and we have more space above, open up
          const openUp = spaceBelow < 350 && spaceAbove > spaceBelow

          setCalendarPosition({
            top: openUp ? undefined : rect.bottom + 4,
            bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
            left: rect.left,
            width: rect.width
          })
        }
      }

      updatePosition()
      window.addEventListener('resize', updatePosition)
      window.addEventListener('scroll', updatePosition, true)

      return () => {
        window.removeEventListener('resize', updatePosition)
        window.removeEventListener('scroll', updatePosition, true)
      }
    } else {
      setCalendarPosition(null)
    }
  }, [open])

  // Handle date selection from calendar
  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date)
      // Convert to ISO format (YYYY-MM-DD)
      const isoDate = date.toISOString().split('T')[0]
      setInputValue(isoDate)
      onChange(isoDate)
      setOpen(false)
    }
  }

  // Handle manual input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value
    setInputValue(inputVal)

    // Try to parse the input as a date
    if (inputVal) {
      const date = new Date(inputVal)
      if (!isNaN(date.getTime())) {
        setSelectedDate(date)
        // Convert to ISO format (YYYY-MM-DD)
        const isoDate = date.toISOString().split('T')[0]
        onChange(isoDate)
      }
    } else {
      setSelectedDate(undefined)
      onChange("")
    }
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  // Close calendar when clicking outside
  React.useEffect(() => {
    if (!open) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      // Check if click is outside both the input wrapper and the calendar popup
      const isOutsideInput = !target.closest('.date-picker-wrapper')
      const isOutsideCalendar = !target.closest('.calendar-popup')
      if (isOutsideInput && isOutsideCalendar) {
        setOpen(false)
      }
    }

    // Use capture phase to catch clicks before they bubble
    document.addEventListener('mousedown', handleClickOutside, true)
    return () => document.removeEventListener('mousedown', handleClickOutside, true)
  }, [open])

  // Close calendar on Escape key
  React.useEffect(() => {
    if (!open) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [open])

  const calendarContent = open && !disabled && calendarPosition && (
    <div
      ref={calendarRef}
      className="calendar-popup fixed z-[9999] bg-gradient-to-br from-black via-black/98 to-black/95 border border-[#4da3ff]/30 rounded-lg shadow-2xl p-4 backdrop-blur-md"
      style={{
        top: calendarPosition.top ? `${calendarPosition.top}px` : undefined,
        bottom: calendarPosition.bottom ? `${calendarPosition.bottom}px` : undefined,
        left: `${calendarPosition.left}px`,
        width: `${Math.max(calendarPosition.width, 320)}px`,
        maxWidth: '380px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.2)'
      }}
    >
      <Calendar
        mode="single"
        selected={selectedDate}
        onSelect={handleDateSelect}
        className="rounded-md border-0 bg-transparent"
        classNames={{
          months: "flex flex-col space-y-4",
          month: "space-y-4",
          caption: "flex justify-center pt-1 pb-3 relative items-center border-b border-white/10 mb-2",
          caption_label: "text-base font-semibold text-white",
          nav: "space-x-1 flex items-center",
          nav_button: cn(
            "h-8 w-8 bg-transparent p-0 opacity-70 hover:opacity-100 text-white border border-white/20 hover:bg-[#4da3ff]/20 hover:border-[#4da3ff]/50 rounded-md transition-all duration-200"
          ),
          nav_button_previous: "absolute left-1",
          nav_button_next: "absolute right-1",
          table: "w-full border-collapse space-y-1",
          head_row: "flex mb-2",
          head_cell: "text-white/70 rounded-md w-10 font-medium text-xs uppercase tracking-wider",
          row: "flex w-full mt-1",
          cell: "h-10 w-10 text-center text-sm p-0 relative",
          day: cn(
            "h-10 w-10 p-0 font-normal text-white hover:bg-[#4da3ff]/30 hover:text-white rounded-md transition-all duration-150 aria-selected:opacity-100 focus:outline-none focus:ring-2 focus:ring-[#4da3ff]/50"
          ),
          day_selected: "bg-[#4da3ff] text-white hover:bg-[#4da3ff]/90 focus:bg-[#4da3ff] font-semibold shadow-lg shadow-[#4da3ff]/30",
          day_today: "bg-white/15 text-white font-semibold border border-white/30",
          day_outside: "text-white/40 opacity-60",
          day_disabled: "text-white/20 opacity-30 cursor-not-allowed",
          day_hidden: "invisible",
        }}
      />
    </div>
  )

  const [isFocused, setIsFocused] = React.useState(false);

  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr + 'T00:00:00');
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="relative date-picker-wrapper" ref={inputRef}>
      <div className="relative">
        <Input
          type="text"
          placeholder={placeholder}
          value={isFocused ? inputValue : (inputValue ? formatDisplayDate(inputValue) : "")}
          onChange={handleInputChange}
          onFocus={() => {
            setIsFocused(true);
            if (!disabled) setOpen(true);
          }}
          onBlur={() => {
            setIsFocused(false);
          }}
          className={cn(
            DATE_INPUT_CLASSNAME,
            "pr-20",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
          disabled={disabled}
        />
        <div className="absolute right-0 top-0 h-full flex items-center gap-1 pr-2">
          {inputValue && !disabled && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 hover:bg-red-500/20 text-white/60 hover:text-red-400 transition-colors"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setInputValue("")
                setSelectedDate(undefined)
                onChange("")
              }}
              title="Clear date"
            >
              ×
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              "h-full px-2 hover:bg-transparent transition-colors",
              open && "text-[#4da3ff]",
              !open && "text-white/50 hover:text-white/70"
            )}
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setOpen(!open)
            }}
            title="Open calendar"
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {typeof window !== 'undefined' && calendarContent && createPortal(calendarContent, document.body)}
    </div>
  )
}
